/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { ConfirmModal } from '@coursera-web/cds-labs';

import { Typography, Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import SetupPanel from 'bundles/author-common/components/setup/SetupPanel';
import ModalButtonFooter from 'bundles/authoring/common/modals/cds/ModalButtonFooter';
import Modal from 'bundles/phoenix/components/Modal';

import _t from 'i18n!nls/author-common';

export type Panel = {
  title: string;
  body: React.ReactNode;
  completed: boolean;
  onNextAlwaysEnabled?: boolean;
  nextLabel?: string;
};

type Props = {
  panels: Array<Panel>;
  heading: string;
  onCancel: () => void;
  onSubmit: () => void;

  skipCancelModal?: boolean;
  description?: React.ReactNode;
  secondaryDescription?: React.ReactNode;
  className?: string;
  errorMessage?: string;

  cancelConfirmationAcceptLabel?: string;
  cancelConfirmationCancelLabel?: string;
  submitLabel?: string;
};

type State = {
  showCancelModal: boolean;
};

class SetupModal extends React.Component<Props, State> {
  state: State = {
    showCancelModal: false,
  };

  handleCancel = () => {
    const { skipCancelModal, onCancel } = this.props;
    if (skipCancelModal) {
      onCancel();
    } else {
      this.setState({ showCancelModal: true });
    }
  };

  render() {
    const { showCancelModal } = this.state;

    const {
      heading,
      description,
      secondaryDescription,
      className,
      panels,
      errorMessage,
      cancelConfirmationAcceptLabel = _t('Leave setup'),
      cancelConfirmationCancelLabel = _t('Stay with setup'),
      submitLabel = _t('Submit'),
      onCancel,
      onSubmit,
    } = this.props;

    const canSubmit = panels.reduce((allCompleted, { completed }) => {
      return completed && allCompleted;
    }, true);

    return (
      <div className="rc-SetupModal">
        {!showCancelModal && (
          <Modal modalName={heading} className={className} handleClose={this.handleCancel}>
            <Typography variant="h1semibold" css={(theme: Theme) => ({ marginBottom: 'var(--cds-spacing-300)' })}>
              {heading}
            </Typography>

            {description && (
              <Typography2 component="p" variant="bodyPrimary">
                {description}
              </Typography2>
            )}
            {secondaryDescription && (
              <Typography2 component="p" variant="bodyPrimary">
                {secondaryDescription}
              </Typography2>
            )}

            {!!errorMessage && <div className="modal-error">{errorMessage}</div>}

            <div>
              {panels.map(({ title, body, completed }, i) => {
                const stepNumber = i + 1;

                return (
                  <SetupPanel stepNumber={stepNumber} title={title} completed={completed}>
                    {body}
                  </SetupPanel>
                );
              })}
            </div>

            <ModalButtonFooter
              onCancelButtonClick={this.handleCancel}
              onPrimaryButtonClick={onSubmit}
              primaryButtonContents={submitLabel}
              isPrimaryButtonDisabled={!canSubmit}
              alignButtonsRight
            />
          </Modal>
        )}
        <ConfirmModal
          open={showCancelModal}
          acceptLabel={cancelConfirmationAcceptLabel}
          cancelLabel={cancelConfirmationCancelLabel}
          onCancel={() => this.setState({ showCancelModal: false })}
          onAccept={onCancel}
        >
          {_t("Are you sure you want to cancel? You'll lose your unsaved changes.")}
        </ConfirmModal>
      </div>
    );
  }
}

export default SetupModal;
